import axios, { AxiosAdapter, AxiosResponse } from 'axios';
import {
  areaDaRota,
  encerrarSessao,
  gravarCrecheSelecionada,
  lerCrecheSelecionada,
  lerToken,
} from './sessao';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
const MOCK_MODE = process.env.REACT_APP_MOCK_MODE === 'true'; // false em produção (build com REACT_APP_MOCK_MODE=false)
const MOCK_DB_KEY = 'gestao-creches-mock-db-v1';

export const isMockMode = MOCK_MODE;

type MockItem = { id: number; [key: string]: any };
type MockDb = Record<string, MockItem[]>;
const DEFAULT_MOCK_PASSWORD = 'Teste@2026';

function parseTokenPayload(token?: string | null): any | null {
  if (!token) return null;
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
}

function hasGlobalTenantAccess(payload: any): boolean {
  const roles = Array.isArray(payload?.roles) ? payload.roles : [];
  const normalized = roles.map((r: unknown) => String(r || '').toLowerCase());
  const tipo = String(payload?.tipo_usuario || '').toLowerCase();
  return normalized.includes('super_admin') || normalized.includes('saas_admin') || tipo === 'super_admin' || tipo === 'saas_admin';
}

const DEFAULT_MOCK_DB: MockDb = {
  '/creches': [{
    id: 1,
    nome: 'Alencastro Consultoria LTDA',
    nome_fantasia: 'Alencastro Consultoria',
    cnpj: '18.785.582/0001-24',
    endereco: 'Florianopolis, SC',
    cep: '88000-000',
    telefone: '',
    celular: '',
    whatsapp: '',
    email: 'suporte@gestaocreche.com.br',
    capacidade_instalada: 0,
    instagram: '',
    facebook: '',
    x: '',
    site_url: 'https://www.gestaocreche.com.br',
    gestores: [],
    // Estrutura Física
    num_salas: 8,
    tem_berçario: true,
    num_berçarios: 2,
    area_total_m2: 450,
    capacidade_por_sala: 'Berçário I: 10, Berçário II: 15, Maternal: 20',
    // Recursos Humanos
    num_educadores: 12,
    num_merendeiras: 3,
    num_faxineiras: 2,
    num_servicos_gerais: 2,
    num_administrativo: 2,
    // Configurações de RH
    cargos_salarios: [
      { cargo: 'Diretor(a)', salario_base: 5500 },
      { cargo: 'Coordenador(a)', salario_base: 4200 },
      { cargo: 'Professor(a)', salario_base: 3100 },
      { cargo: 'Monitor(a)', salario_base: 1800 },
      { cargo: 'Cozinheiro(a)', salario_base: 2200 },
      { cargo: 'Auxiliar de Limpeza', salario_base: 1600 }
    ],
    // Ciclo de Vida do Cliente SaaS
    status: 'ativa', // enum CrecheEntity: 'pendente' | 'ativa' | 'suspensa' | 'negada'
    plano_id: 2, // 1: Bronze, 2: Prata, 3: Ouro
    contrato_assinado: true,
    data_formalizacao: '2026-01-15'
  }],
  '/turmas': [],
  '/alunos': [],
  '/financeiro/mensalidades': [],
  '/cardapios': [],
  '/calendario': [],
  '/comunicacao': [],
  '/atendimentos-especiais': [],
  '/rotina': [],
  '/ia/insights': [],
  '/ia/alertas': [],
  '/ia/materiais': [],
  '/ia/configuracao': [{
    id: 1,
    creche_id: 1,
    nome_assistente: 'Assistente Alencastro',
    mensagem_boas_vindas_publica: 'Ola! Este e o ambiente demonstrativo institucional da Alencastro Consultoria.',
    mensagem_boas_vindas_interna: 'Ambiente interno de demonstracao sem dados pessoais reais de clientes.',
    horario_funcionamento: 'Segunda a sexta, das 08h as 18h.',
    formas_atuacao: 'Consultoria, implantacao e suporte da plataforma Gestao Creches.',
    conteudo_site_publico: 'Alencastro Consultoria - CNPJ 18.785.582/0001-24 - Florianopolis/SC - Site: www.gestaocreche.com.br',
    contato_publico: 'suporte@gestaocreche.com.br | comercial@gestaocreche.com.br',
    topicos_publicos_liberados: ['horarios', 'cardapio', 'contato', 'atuacao', 'site'],
    perfis_internos_habilitados: ['gestor', 'admin', 'professor', 'administrativo'],
    audio_entrada_publico: true,
    audio_saida_publico: true,
    audio_entrada_interno: true,
    audio_saida_interno: true,
    voz_publica: '',
    voz_interna: '',
    instrucoes_publicas: 'Você é a Liz Master, a Super Assistente Estratégica da Alencastro Consultoria. DIRETRIZ DNA V2.0: 1. PRIORIDADE: Base de treinamento interna. 2. EXPANSÃO: Se a resposta não estiver na base interna, use sua capacidade de Pesquisa Web em tempo real. 3. FUNDAMENTAÇÃO RÍGIDA: Toda resposta (interna ou web) deve ser estritamente fundamentada em legislação ou fontes oficiais. É PROIBIDO inventar ou alucinar. 4. CITAÇÕES: O botão "Verificar Fonte" é obrigatório para cada orientação jurídica ou de marketing. Se buscar na web, cite o link direto (Ex: Planalto, MEC, Diário Oficial). Preferência absoluta por fontes governamentais e acadêmicas de alta credibilidade.',
    perfil_conversa_interna: 'parceria_pedagogica',
    instrucoes_internas: 'Consultora de precisão cirúrgica. Ao usar a web, filtre o ruído e foque na lei seca e dados comprovados. Zero tolerância para "opiniões" de IA não fundamentadas.',
    gemini_api_key: '',
  }],
  '/users': [{
    id: 1,
    creche_id: 1,
    nome: 'Diogo Alencastro',
    cpf: '25325159020',
    email: 'diogo@gestaocreche.com.br',
    senha: DEFAULT_MOCK_PASSWORD,
    whatsapp: '',
    perfil: 'gestor',
    tipo_usuario: 'gestor',
    ativo: true,
  }, {
    id: 2,
    creche_id: 0,
    nome: 'Admin SaaS',
    cpf: '00000000000',
    email: 'admin@gestaocreche.com.br',
    senha: DEFAULT_MOCK_PASSWORD,
    whatsapp: '',
    perfil: 'saas_admin',
    tipo_usuario: 'saas_admin',
    ativo: true,
  }],
  '/familias/responsaveis': [],
  '/familias/contatos-emergencia': [],
  '/familias/autorizacoes': [],
  '/familias/agenda': [],
  '/funcionarios': [],
  '/funcionarios/cargos': [],
  '/funcionarios/ponto': [],
  '/funcionarios/escalas': [],
  '/funcionarios/capacitacoes': [],
  '/pedagogico/projeto': [],
  '/pedagogico/planejamento': [],
  '/pedagogico/avaliacoes': [],
  '/pedagogico/portfolio': [],
  '/administrativo/financeiro': [],
  '/administrativo/inventario': [],
  '/administrativo/licencas': [],
  '/administrativo/relatorios': [],
  '/seguranca/acesso': [],
  '/seguranca/monitoramento': [],
  '/seguranca/protocolos': [],
  '/seguranca/contatos-emergencia': [],
};

function ensureDbCompatibility(db: MockDb): MockDb {
  const next: MockDb = { ...db };

  Object.keys(DEFAULT_MOCK_DB).forEach((resource) => {
    if (!Array.isArray(next[resource])) {
      next[resource] = [...DEFAULT_MOCK_DB[resource]];
    }
  });

  next['/creches'] = (next['/creches'] || []).map((item, index) => ({
    ...item,
    id: item.id || index + 1,
    gestores: Array.isArray(item.gestores) ? item.gestores : [],
  }));

  next['/users'] = (next['/users'] || []).map((item, index) => ({
    ...item,
    id: item.id || index + 1,
    creche_id: Number(item.creche_id ?? 1),
    tipo_usuario: String(item.tipo_usuario || item.perfil || (Number(item.creche_id ?? 1) === 0 ? 'saas_admin' : 'gestor')),
    senha: String(item.senha || DEFAULT_MOCK_PASSWORD),
  }));

  const applyCrecheId = (resource: string) => {
    next[resource] = (next[resource] || []).map((item, index) => ({
      ...item,
      id: item.id || index + 1,
      creche_id: Number(item.creche_id || 1),
    }));
  };

  applyCrecheId('/turmas');
  applyCrecheId('/alunos');
  applyCrecheId('/financeiro/mensalidades');
  applyCrecheId('/cardapios');
  applyCrecheId('/calendario');
  applyCrecheId('/comunicacao');
  applyCrecheId('/atendimentos-especiais');
  applyCrecheId('/rotina');
  applyCrecheId('/ia/insights');
  applyCrecheId('/ia/alertas');
  applyCrecheId('/ia/materiais');
  applyCrecheId('/ia/configuracao');
  applyCrecheId('/users');

  return next;
}

function readDb(): MockDb {
  const raw = localStorage.getItem(MOCK_DB_KEY);
  if (!raw) {
    localStorage.setItem(MOCK_DB_KEY, JSON.stringify(DEFAULT_MOCK_DB));
    return { ...DEFAULT_MOCK_DB };
  }
  try {
    const parsed = JSON.parse(raw) as MockDb;
    const compatible = ensureDbCompatibility(parsed);
    localStorage.setItem(MOCK_DB_KEY, JSON.stringify(compatible));
    return compatible;
  } catch {
    localStorage.setItem(MOCK_DB_KEY, JSON.stringify(DEFAULT_MOCK_DB));
    return { ...DEFAULT_MOCK_DB };
  }
}

function writeDb(db: MockDb) {
  localStorage.setItem(MOCK_DB_KEY, JSON.stringify(db));
}

export function resetMockDatabase() {
  if (!MOCK_MODE) return;
  localStorage.setItem(MOCK_DB_KEY, JSON.stringify(DEFAULT_MOCK_DB));
}

export function getCurrentTenantId(tokenOverride?: string | null): number {
  const token = tokenOverride ?? lerToken();
  const payload = parseTokenPayload(token);
  if (!payload) return 0;
  return Number(payload.id_creche || 0);
}

// Token e creche selecionada vivem no escopo (aba × produto) — ver services/sessao.ts.
export function getCrecheSelecionada(): number {
  return lerCrecheSelecionada();
}

export function setCrecheSelecionada(idCreche: number | null) {
  gravarCrecheSelecionada(idCreche);
}

// Para super_admin/saas_admin (sem creche própria), o tenant "efetivo" vem da
// creche selecionada no painel (seletor de creche). Para os demais usuários,
// o tenant é sempre o id_creche do próprio token — nunca a seleção local.
export function getEffectiveTenantId(tokenOverride?: string | null): number {
  const token = tokenOverride ?? lerToken();
  const payload = parseTokenPayload(token);
  if (hasGlobalTenantAccess(payload)) return getCrecheSelecionada();
  return getCurrentTenantId(token);
}

function getTokenFromAuthHeader(authHeader?: string) {
  if (!authHeader) return null;
  const parts = authHeader.split(' ');
  return parts.length === 2 ? parts[1] : null;
}

function parseUrl(url?: string) {
  if (!url) return { resource: '', id: null, normalized: '' };
  
  // Remove baseURL ou origem absoluta para pegar apenas o path
  let path = url;
  if (path.includes('://')) {
    try {
      const u = new URL(path);
      path = u.pathname + u.search;
    } catch {
      path = path.replace(API_BASE_URL, '');
    }
  } else {
    path = path.replace(API_BASE_URL, '');
  }

  const normalized = path.startsWith('/') ? path : `/${path}`;
  const cleanPath = normalized.split('?')[0];
  const parts = cleanPath.split('/').filter(Boolean);
  
  // Lógica de Resource vs ID
  // Se o último segmento for número, é ID. 
  // EXCETO para rotas conhecidas sem ID como /auth/recuperar-senha
  const lastSegment = parts[parts.length - 1];
  const lastIsNumber = /^\d+$/.test(lastSegment || '');
  const id = lastIsNumber ? Number(lastSegment) : null;
  const resource = `/${parts.slice(0, id !== null ? -1 : parts.length).join('/')}`;
  
  return { resource, id, normalized: cleanPath };
}

function fakeToken(user: Record<string, any>) {
  const email = String(user.email || '').toLowerCase();
  const tipoUsuario = String(user.tipo_usuario || user.perfil || 'gestor').toLowerCase();
  const idCreche = Number(user.id_creche ?? user.creche_id ?? (tipoUsuario === 'saas_admin' || tipoUsuario === 'super_admin' ? 0 : 1));
  const header = btoa(JSON.stringify({ alg: 'none', typ: 'JWT' }));
  const payload = btoa(JSON.stringify({
    sub: Number(user.id || 1),
    email,
    id_creche: idCreche,
    roles: [tipoUsuario],
    tipo_usuario: tipoUsuario,
  }));
  return `${header}.${payload}.mock`;
}

function parseData(raw: unknown) {
  if (!raw) return {};
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }
  return raw as Record<string, unknown>;
}

const mockAdapter: AxiosAdapter = async (config) => {
  const method = (config.method || 'get').toLowerCase();
  const { resource, id, normalized } = parseUrl(config.url);
  const db = readDb();
  const data = parseData(config.data);
  const authHeader = (config.headers as Record<string, string> | undefined)?.Authorization;

  // ─── Rotas de Autenticação (Login e Recuperação) ──────────────────

  // NOVO: Registro de administrador SaaS
  if (resource === '/auth/saas-register' && method === 'post') {
    const users = db['/users'] || [];
    const email = String((data as any).email || '').toLowerCase();

    // Verificar se email já existe
    if (users.some(u => String(u.email || '').toLowerCase() === email)) {
      return Promise.reject({
        response: { status: 409, data: { message: 'Este e-mail já está cadastrado.' } },
        config,
        isAxiosError: true,
      });
    }

    // Criar novo usuário
    const newUser = {
      id: Math.max(0, ...users.map(u => u.id || 0)) + 1,
      nome: String((data as any).nome || ''),
      email: email,
      senha: String((data as any).senha || ''), // Em produção, seria hash
      cpf: String((data as any).cpf || ''),
      rg: String((data as any).rg || ''),
      rg_uf: String((data as any).rg_uf || ''),
      tipo_usuario: 'super_admin',
      ativo: true,
      criado_em: new Date().toISOString(),
    };

    users.push(newUser);
    db['/users'] = users;
    writeDb(db);

    return {
      data: {
        message: 'Administrador registrado com sucesso',
        user: { id: newUser.id, email: newUser.email, nome: newUser.nome }
      },
      status: 201, statusText: 'Created', headers: {}, config,
    };
  }

  if (resource === '/auth/login' && method === 'post') {
    const email = String((data as any).email || '').toLowerCase();
    const senha = String((data as any).senha || '');
    const users = db['/users'] || [];

    if (!senha.trim()) {
      return Promise.reject({
        response: { status: 401, data: { message: 'E-mail ou senha inválidos' } },
        config,
        isAxiosError: true,
      });
    }

    // Buscar usuário por email
    const user = users.find(u => String(u.email || '').toLowerCase() === email);

    if (user && user.ativo !== false && String(user.senha || '').trim() && String(user.senha || '') === senha) {
      const access_token = fakeToken(user);
      const requer_lgpd = false; // Desativado para simplificar
      const tipoUsuario = String(user.tipo_usuario || user.perfil || 'gestor').toLowerCase();

      const response: AxiosResponse = {
        data: {
          token: access_token,
          access_token,
          user: {
            id: user.id,
            email: user.email,
            nome: user.nome,
            tipo_usuario: tipoUsuario,
            id_creche: Number(user.creche_id || 0)
          },
          requer_consentimento_lgpd: requer_lgpd,
          termo_lgpd_pendente: null
        },
        status: 200, statusText: 'OK', headers: {}, config,
      };
      return response;
    }

    return Promise.reject({
      response: { status: 401, data: { message: 'E-mail ou senha inválidos' } },
      config,
      isAxiosError: true,
    });
  }

  if (resource === '/auth/recuperar-senha' && method === 'post') {
    const email = String((data as any).email || '');
    const users = db['/users'] || [];
    const exists = users.some(u => String(u.email || '').toLowerCase() === email.toLowerCase());
    
    return {
      data: { 
        mensagem: exists 
          ? 'Instruções de redefinição enviadas para seu e-mail. Verifique sua caixa de entrada.' 
          : 'Se este e-mail estiver cadastrado, você receberá as instruções em breve.' 
      },
      status: 200, statusText: 'OK', headers: {}, config,
    };
  }

  if (resource === '/auth/recuperar-login' && method === 'post') {
    const cpf = String((data as any).cpf || '');
    const nome = String((data as any).nome || '');
    
    if (!cpf && !nome) {
      return Promise.reject({
        response: { status: 400, data: { message: 'Informe o CPF ou o nome completo cadastrado.' } },
        config,
        isAxiosError: true,
      });
    }

    const users = db['/users'] || [];
    const found = users.find(u => {
      const uCpf = String(u.cpf || '').replace(/\D/g, '');
      const sCpf = cpf.replace(/\D/g, '');
      if (sCpf && uCpf === sCpf) return true;
      if (nome && String(u.nome || '').toLowerCase().includes(nome.toLowerCase())) return true;
      return false;
    });

    if (found) {
      const email = String(found.email || '');
      const [local, dom] = email.split('@');
      const masked = local.length > 2 ? local[0] + '***' + local[local.length-1] : '***';
      return {
        data: { 
          email_mascarado: `${masked}@${dom}`,
          mensagem: 'Encontramos um cadastro. O e-mail de acesso associado é:' 
        },
        status: 200, statusText: 'OK', headers: {}, config,
      };
    }

    return {
      data: { mensagem: 'Nenhum cadastro encontrado com os dados informados. Entre em contato com o administrador da sua creche.' },
      status: 200, statusText: 'OK', headers: {}, config,
    };
  }

  if (resource === '/lgpd/primeiro-acesso/status' && method === 'get') {
    return {
      data: {
        termo: {
          id: 1,
          titulo: 'Termo de Privacidade e Proteção de Dados',
          versao: 1,
          conteudo: `Este Termo de Privacidade regula o tratamento de dados pessoais realizado pela Alencastro Consultoria no âmbito do sistema Gestão Creches.

1. FINALIDADE: Tratamento necessário para a execução do contrato de prestação de serviços educacionais e gestão da creche.
2. DADOS TRATADOS: Nome, CPF, Endereço e dados de contato dos responsáveis e alunos.
3. SEGURANÇA: Todos os dados são criptografados e armazenados em ambiente seguro de acordo com a LGPD.
4. DIREITOS: Você pode solicitar acesso, correção ou exclusão de seus dados a qualquer momento via suporte.
5. RETENÇÃO: Mantemos os dados apenas pelo tempo necessário para cumprir obrigações legais e regulatórias.

Ao clicar em 'Aceitar', você confirma que leu e concorda com estas diretrizes.`,
        }
      },
      status: 200, statusText: 'OK', headers: {}, config,
    };
  }

  if (resource === '/lgpd/primeiro-acesso/aceitar' && method === 'post') {
    return {
      data: { success: true, mensagem: 'Consentimento registrado com sucesso.' },
      status: 201, statusText: 'Created', headers: {}, config,
    };
  }

  // ─── Outras Rotas Mock ───────────────────────────────────────────
  const publicConfigMatch = normalized.match(/^\/ia\/publico\/configuracao\/(\d+)$/);
  if (publicConfigMatch && method === 'get') {
    const crecheId = Number(publicConfigMatch[1]);
    const configuracao = (db['/ia/configuracao'] || []).find((item) => Number(item.creche_id) === crecheId);
    const creche = (db['/creches'] || []).find((item) => item.id === crecheId);
    const cardapios = (db['/cardapios'] || []).filter((item) => Number(item.creche_id) === crecheId);
    return {
      data: {
        ...(configuracao || {}),
        id_creche: crecheId,
        creche,
        cardapios,
      },
      status: 200,
      statusText: 'OK',
      headers: {},
      config,
    };
  }

  if (resource === '/ia/publico/chat' && method === 'post') {
    const crecheId = Number((data as any).id_creche || 1);
    const pergunta = String((data as any).pergunta || '');
    const configuracao = ((db['/ia/configuracao'] || []).find((item) => Number(item.creche_id) === crecheId) || {}) as Record<string, any>;
    const cardapios = (db['/cardapios'] || []).filter((item) => Number(item.creche_id) === crecheId);
    const materiais = (db['/ia/materiais'] || []).filter((item) => Number(item.creche_id) === crecheId && ['publico', 'ambos'].includes(String(item.audiencia || 'interno')) && String(item.status_validacao || '') === 'aprovado');
    const resposta = [
      configuracao.mensagem_boas_vindas_publica || 'Olá! Posso ajudar com informações públicas da creche.',
      `Pergunta recebida: "${pergunta}".`,
      `Horário: ${configuracao.horario_funcionamento || 'não informado.'}`,
      `Atuação: ${configuracao.formas_atuacao || 'não informada.'}`,
      `Contato: ${configuracao.contato_publico || 'não informado.'}`,
      cardapios.length ? `Cardápio da semana: ${cardapios.map((item) => `${item.dia_semana}: almoço ${item.almoco || '-'}`).join(' | ')}` : 'Cardápio ainda não publicado.',
      materiais.length ? `Materiais públicos validados: ${materiais.slice(0, 3).map((item) => item.titulo).join(', ')}.` : 'Não há materiais públicos adicionais liberados no momento.',
    ].join('\n\n');
    return {
      data: {
        resposta,
        audio: {
          entrada_habilitada: configuracao.audio_entrada_publico !== false,
          saida_habilitada: configuracao.audio_saida_publico !== false,
          voz: configuracao.voz_publica || '',
        },
      },
      status: 200,
      statusText: 'OK',
      headers: {},
      config,
    };
  }

  if (!authHeader) {
    return Promise.reject({
      response: { status: 401, data: { message: 'Não autenticado' } },
      config,
      isAxiosError: true,
    });
  }

  const tenantId = getCurrentTenantId(getTokenFromAuthHeader(authHeader));
  const tokenPayload = parseTokenPayload(getTokenFromAuthHeader(authHeader));
  const canAccessAllTenants = hasGlobalTenantAccess(tokenPayload);

  if (resource === '/ia/configuracao' && method === 'get') {
    const configuracao = ((db['/ia/configuracao'] || []).find((item) => Number(item.creche_id) === tenantId) || null) as Record<string, any> | null;
    return { data: configuracao, status: 200, statusText: 'OK', headers: {}, config };
  }

  if (resource === '/ia/configuracao' && method === 'put') {
    const collection = db['/ia/configuracao'] || [];
    const atual = collection.find((item) => Number(item.creche_id) === tenantId);
    const atualizado = {
      id: atual?.id || 1,
      creche_id: tenantId,
      ...(atual || {}),
      ...(data as object),
    } as MockItem;
    db['/ia/configuracao'] = [
      ...collection.filter((item) => Number(item.creche_id) !== tenantId),
      atualizado,
    ];
    writeDb(db);
    return { data: atualizado, status: 200, statusText: 'OK', headers: {}, config };
  }

  if (resource === '/ia/chat' && method === 'post') {
    const insights = (db['/ia/insights'] || []).filter((i) => Number(i.creche_id) === tenantId);
    const alertas = (db['/ia/alertas'] || []).filter((a) => Number(a.creche_id) === tenantId);
    const configuracao = ((db['/ia/configuracao'] || []).find((item) => Number(item.creche_id) === tenantId) || {}) as Record<string, any>;
    const materiais = (db['/ia/materiais'] || []).filter(
      (m) => Number(m.creche_id) === tenantId && String(m.status_validacao || '') === 'aprovado' && ['interno', 'ambos'].includes(String(m.audiencia || 'interno')),
    );

    const resposta = [
      configuracao.mensagem_boas_vindas_interna || 'Olá equipe! Posso ajudar com orientações internas validadas.',
      `Pergunta recebida: "${String((data as any).pergunta || '')}"`,
      `Insights: ${insights.length}`,
      `Alertas: ${alertas.length}`,
      `Materiais validados: ${materiais.length}`,
      'Sugestão: priorize alertas críticos e acompanhe os insights mais recentes.',
    ].join('\n');

    const response: AxiosResponse = {
      data: {
        resposta,
        contexto: { insights: insights.length, alertas: alertas.length, materiais_validados: materiais.length },
        audio: {
          entrada_habilitada: configuracao.audio_entrada_interno !== false,
          saida_habilitada: configuracao.audio_saida_interno !== false,
          voz: configuracao.voz_interna || '',
        },
      },
      status: 200,
      statusText: 'OK',
      headers: {},
      config,
    };
    return response;
  }

  if (resource === '/ia/materiais/upload' && method === 'post') {
    const collection = db['/ia/materiais'] || [];
    const nextId = collection.length > 0 ? Math.max(...collection.map((item) => item.id)) + 1 : 1;
    const form = config.data as any;
    const titulo = typeof form?.get === 'function' ? String(form.get('titulo') || 'Material IA') : 'Material IA';
    const descricao = typeof form?.get === 'function' ? String(form.get('descricao') || '') : '';

    const created = {
      id: nextId,
      creche_id: tenantId,
      titulo,
      descricao,
      status_validacao: 'pendente',
      tipo_arquivo: 'outro',
      tipo_material: 'arquivo',
      audiencia: typeof form?.get === 'function' ? String(form.get('audiencia') || 'interno') : 'interno',
      nome_arquivo_original: 'arquivo-enviado',
      criado_em: new Date().toISOString(),
    } as MockItem;

    db['/ia/materiais'] = [...collection, created];
    writeDb(db);

    const response: AxiosResponse = {
      data: created,
      status: 201,
      statusText: 'Created',
      headers: {},
      config,
    };
    return response;
  }

  if (resource === '/ia/materiais/link' && method === 'post') {
    const collection = db['/ia/materiais'] || [];
    const nextId = collection.length > 0 ? Math.max(...collection.map((item) => item.id)) + 1 : 1;
    const url = String((data as any).url_link || '');
    const titulo = String((data as any).titulo || url.split('/')[2] || 'Link de Referência');
    const descricao = String((data as any).descricao || '');

    const created = {
      id: nextId,
      creche_id: tenantId,
      titulo,
      descricao,
      url_link: url,
      status_validacao: 'pendente',
      tipo_arquivo: 'link',
      tipo_material: 'link',
      audiencia: String((data as any).audiencia || 'interno'),
      nome_arquivo_original: url.slice(0, 255),
      caminho_arquivo: url,
      criado_em: new Date().toISOString(),
    } as MockItem;

    db['/ia/materiais'] = [...collection, created];
    writeDb(db);

    const response: AxiosResponse = {
      data: created,
      status: 201,
      statusText: 'Created',
      headers: {},
      config,
    };
    return response;
  }

  if (resource === '/ia/materiais/video' && method === 'post') {
    const collection = db['/ia/materiais'] || [];
    const nextId = collection.length > 0 ? Math.max(...collection.map((item) => item.id)) + 1 : 1;
    const url = String((data as any).url_video || '');
    const titulo = String((data as any).titulo || 'Vídeo de Treinamento');
    const descricao = String((data as any).descricao || '');

    const created = {
      id: nextId,
      creche_id: tenantId,
      titulo,
      descricao,
      url_video: url,
      status_validacao: 'pendente',
      tipo_arquivo: 'video',
      tipo_material: 'video',
      audiencia: String((data as any).audiencia || 'interno'),
      nome_arquivo_original: url.slice(0, 255),
      caminho_arquivo: url,
      criado_em: new Date().toISOString(),
    } as MockItem;

    db['/ia/materiais'] = [...collection, created];
    writeDb(db);

    const response: AxiosResponse = {
      data: created,
      status: 201,
      statusText: 'Created',
      headers: {},
      config,
    };
    return response;
  }

  const materialValidacaoMatch = normalized.match(/^\/ia\/materiais\/(\d+)\/validacao$/);
  if (materialValidacaoMatch && method === 'put') {
    const materialId = Number(materialValidacaoMatch[1]);
    const collection = db['/ia/materiais'] || [];
    const target = collection.find((item) => item.id === materialId && Number(item.creche_id) === tenantId);
    if (!target) {
      return Promise.reject({ response: { status: 404, data: { message: 'Material nao encontrado' } }, config, isAxiosError: true });
    }

    db['/ia/materiais'] = collection.map((item) => (
      item.id === materialId
        ? {
            ...item,
            ...(data as object),
            id: materialId,
            creche_id: tenantId,
          }
        : item
    ));
    writeDb(db);

    const updated = db['/ia/materiais'].find((item) => item.id === materialId) || null;
    const response: AxiosResponse = {
      data: updated,
      status: 200,
      statusText: 'OK',
      headers: {},
      config,
    };
    return response;
  }

  const collection = db[resource] || [];
  const isCrecheCollection = resource === '/creches';
  // Em /creches: apenas super_admin/saas_admin podem ver todas as creches.
  // Gestor de creche vê estritamente a própria creche.
  const scopedCollection = collection.filter((item) => {
    if (isCrecheCollection) {
      if (canAccessAllTenants) return true;
      return Number(item.id) === tenantId;
    }
    if (item.creche_id === undefined) return true;
    return Number(item.creche_id) === tenantId;
  });

  if (method === 'get') {
    let result = id ? scopedCollection.find((item) => item.id === id) : scopedCollection;
    if (!id && Array.isArray(result) && (resource === '/ia/insights' || resource === '/ia/alertas')) {
      const params = (config.params || {}) as Record<string, any>;
      if (params.id_turma) {
        result = result.filter((item) => Number(item.id_turma || 0) === Number(params.id_turma));
      }
      if (params.id_aluno) {
        result = result.filter((item) => Number(item.id_aluno || 0) === Number(params.id_aluno));
      }
    }

    if (!id && Array.isArray(result) && resource === '/ia/materiais') {
      const params = (config.params || {}) as Record<string, any>;
      if (params.status_validacao) {
        result = result.filter((item) => String(item.status_validacao || '') === String(params.status_validacao));
      }
    }

    const response: AxiosResponse = {
      data: result ?? (id ? null : []),
      status: 200,
      statusText: 'OK',
      headers: {},
      config,
    };
    return response;
  }

  if (method === 'post') {
    if (isCrecheCollection && !canAccessAllTenants) {
      return Promise.reject({ response: { status: 403, data: { message: 'Acesso negado para criar creche' } }, config, isAxiosError: true });
    }

    const nextId = collection.length > 0 ? Math.max(...collection.map((item) => item.id)) + 1 : 1;
    const payload = data as Record<string, unknown>;
    const created = {
      id: nextId,
      ...payload,
      ...(isCrecheCollection ? {} : { creche_id: tenantId }),
    } as MockItem;
    db[resource] = [...collection, created];
    writeDb(db);
    const response: AxiosResponse = {
      data: created,
      status: 201,
      statusText: 'Created',
      headers: {},
      config,
    };
    return response;
  }

  if (method === 'put' && id) {
    const target = collection.find((item) => item.id === id);
    if (!target) {
      return Promise.reject({ response: { status: 404, data: { message: 'Registro não encontrado' } }, config, isAxiosError: true });
    }
    if (isCrecheCollection && !canAccessAllTenants) {
      return Promise.reject({ response: { status: 403, data: { message: 'Acesso negado para editar creche' } }, config, isAxiosError: true });
    }
    if (!isCrecheCollection && Number(target.creche_id) !== tenantId) {
      return Promise.reject({ response: { status: 403, data: { message: 'Acesso negado para outra creche' } }, config, isAxiosError: true });
    }
    db[resource] = collection.map((item) => (
      item.id === id
        ? {
            ...item,
            ...(data as object),
            id,
            ...(isCrecheCollection ? {} : { creche_id: tenantId }),
          }
        : item
    ));
    writeDb(db);
    const updated = db[resource].find((item) => item.id === id) || null;
    const response: AxiosResponse = {
      data: updated,
      status: 200,
      statusText: 'OK',
      headers: {},
      config,
    };
    return response;
  }

  if (method === 'delete' && id) {
    const target = collection.find((item) => item.id === id);
    if (!target) {
      return Promise.reject({ response: { status: 404, data: { message: 'Registro não encontrado' } }, config, isAxiosError: true });
    }
    if (isCrecheCollection && !canAccessAllTenants) {
      return Promise.reject({ response: { status: 403, data: { message: 'Acesso negado para remover creche' } }, config, isAxiosError: true });
    }
    if (!isCrecheCollection && Number(target.creche_id) !== tenantId) {
      return Promise.reject({ response: { status: 403, data: { message: 'Acesso negado para outra creche' } }, config, isAxiosError: true });
    }
    db[resource] = collection.filter((item) => item.id !== id);
    writeDb(db);
    const response: AxiosResponse = {
      data: { success: true },
      status: 200,
      statusText: 'OK',
      headers: {},
      config,
    };
    return response;
  }

  return Promise.reject({
    response: { status: 404, data: { message: 'Rota não suportada no mock' } },
    config,
    isAxiosError: true,
  });
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  adapter: MOCK_MODE ? mockAdapter : undefined,
});

/**
 * A sessão é isolada por (aba × produto) em `services/sessao.ts`: o token que
 * esta aba usa não muda porque alguém logou em outra aba, e `/app` e `/saas`
 * têm slots próprios. Por isso não existe mais o guarda de "sessão trocada em
 * outra aba" (que bloqueava a aba com 409 e exigia F5) — o conflito que ele
 * detectava deixou de ser possível.
 */
api.interceptors.request.use((config) => {
  const token = lerToken();

  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
    const tenantId = getEffectiveTenantId(token);
    config.headers['x-tenant-id'] = String(tenantId || 0);
  }

  const tenantId = getEffectiveTenantId(token);
  const isCrecheEndpoint = (config.url || '').includes('/creches');
  const isWrite = ['post', 'put', 'patch'].includes((config.method || 'get').toLowerCase());
  if (tenantId && isWrite && !isCrecheEndpoint && config.data) {
    if (typeof config.data === 'string') {
      try {
        const parsed = JSON.parse(config.data);
        config.data = JSON.stringify({ ...parsed, id_creche: tenantId });
      } catch {
        // noop
      }
    } else if (typeof config.data === 'object') {
      config.data = { ...(config.data as Record<string, unknown>), id_creche: tenantId };
    }
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const pathname = window.location.pathname || '';
      const isLoginRoute = pathname.startsWith('/login') || pathname.startsWith('/saas/login');
      if (isLoginRoute) {
        return Promise.reject(error);
      }

      // Encerra só a sessão do produto desta aba (a outra aba/produto segue).
      const area = areaDaRota(pathname);
      encerrarSessao(area);
      window.location.href = area === 'saas' ? '/saas/login' : '/login';
    }
    return Promise.reject(error);
  },
);

export default api;
